import tw from 'twin.macro';

import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { EyeIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { ChevronDoubleLeftIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { BackofficeSession, SessionStatus } from '@/libs/graphql';
import getStatusStyle from '@/utils/getStatusStyle';
import { getSessionStatusText } from '@/utils/getSessionStatusText';

const columnHelper = createColumnHelper<Partial<BackofficeSession>>();

const columns = [
  columnHelper.display({
    id: 'candidate',
    header: () => <span>ชื่อผู้สมัคร / รายละเอียด</span>,
    cell: ({ row }) => (
      <div tw="flex flex-col gap-0.5">
        <span tw="text-base font-bold text-gray-900">{row.original.intervieweeName ?? '—'}</span>
        <span tw="text-sm text-gray-500">{row.original.intervieweeEmail ?? ''}</span>
        <span tw="text-xs text-gray-400 font-mono uppercase mt-0.5">
          Ref: {row.original.refId ?? '—'}
        </span>
      </div>
    ),
    size: 220,
  }),
  columnHelper.display({
    id: 'positionTemplate',
    header: () => <span>ตำแหน่ง &amp; ชุดคำถาม</span>,
    cell: ({ row }) => (
      <div tw="flex flex-col gap-1">
        <span tw="text-sm font-medium text-gray-800">{row.original.position ?? '—'}</span>
        <div tw="flex items-center gap-1.5 text-gray-500">
          <QuestionMarkCircleIcon tw="h-4 w-4 shrink-0" />
          <span tw="text-xs">{row.original.questionTemplateName ?? '—'}</span>
        </div>
      </div>
    ),
    size: 200,
  }),
  columnHelper.display({
    id: 'status',
    header: () => <span>สถานะ</span>,
    cell: ({ row }) => {
      const statusText = getSessionStatusText(
        row.original.status as SessionStatus,
        row.original.availableUntil ?? null,
        row.original.expiredAt,
      );
      return (
        <span
          css={[
            tw`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold`,
            getStatusStyle(statusText),
          ]}
        >
          {statusText}
        </span>
      );
    },
    size: 140,
  }),
  columnHelper.accessor('createdAt', {
    header: () => <span>วันที่รับสมัคร</span>,
    cell: (props) => (
      <span tw="text-sm text-gray-500">
        {props.getValue() ? dayjs(props.getValue()).format('DD/MM/YYYY') : '—'}
      </span>
    ),
    size: 120,
  }),
  columnHelper.accessor((row) => row?.aiMetadata?.highlightCount, {
    id: 'highlights',
    header: () => <span tw="block text-center">ไฮไลท์</span>,
    cell: (props) => {
      const val = props.getValue();
      return (
        <p tw="text-center font-bold text-blue-700">{val != null ? val : '—'}</p>
      );
    },
    size: 80,
  }),
  columnHelper.accessor('id', {
    header: () => <span tw="block text-right">ดำเนินการ</span>,
    cell: (props) => (
      <div tw="flex justify-end">
        <button
          type="button"
          tw="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-amber-600 font-bold text-sm hover:(bg-amber-500 text-white border-amber-500) transition-all shadow-sm"
          onClick={() =>
            window.open(`/backoffice/interviewee/${props.getValue()}`, '_blank', 'noopener,noreferrer')
          }
        >
          <EyeIcon tw="h-4 w-4" />
          ดูรายละเอียด
        </button>
      </div>
    ),
    size: 160,
  }),
];

const CandidateSessionDataTable = ({
  sessions,
  total = 0,
  currentPage = 1,
  pageSize = 50,
  hasNextPage = false,
  hasPrevPage = false,
  onNext,
  onPrev,
  onReset,
}: {
  sessions?: Partial<BackofficeSession>[];
  total?: number;
  currentPage?: number;
  pageSize?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  onReset?: () => void;
}) => {
  const table = useReactTable({
    data: (sessions ?? []) as Partial<BackofficeSession>[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = total > 0 ? Math.ceil(total / pageSize) : 1;
  const startItem = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, total);

  return (
    <div tw="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div tw="overflow-x-auto">
        <table tw="w-full text-left border-collapse">
          <thead tw="bg-gray-50 border-b border-gray-200">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                    tw="py-4 px-4 text-xs font-bold text-gray-500 uppercase tracking-wider"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody tw="divide-y divide-gray-100">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} tw="text-center py-12 text-gray-400 text-sm">
                  ไม่พบข้อมูล
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  tw="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() =>
                    window.open(
                      `/backoffice/interviewee/${row.original.id}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} tw="px-4 py-4">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > 0 && (
        <div tw="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col md:(flex-row justify-between) items-center gap-4">
          <p tw="text-sm text-gray-500 font-medium">
            แสดง <span tw="font-bold text-gray-900">{startItem}–{endItem}</span> จาก{' '}
            <span tw="font-bold text-gray-900">{total}</span> รายการ
          </p>
          <div tw="flex items-center gap-2">
            <button
              onClick={onReset}
              disabled={!hasPrevPage}
              tw="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-400 disabled:(opacity-30 cursor-not-allowed) hover:not-disabled:bg-gray-100 transition-colors"
            >
              <ChevronDoubleLeftIcon tw="h-4 w-4" />
            </button>
            <button
              onClick={onPrev}
              disabled={!hasPrevPage}
              tw="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-400 disabled:(opacity-30 cursor-not-allowed) hover:not-disabled:bg-gray-100 transition-colors"
            >
              <ChevronLeftIcon tw="h-4 w-4" />
            </button>
            <div tw="flex items-center bg-white border border-gray-200 rounded-xl px-4 h-10">
              <span tw="text-sm font-bold text-gray-700">
                หน้า {currentPage} / {totalPages}
              </span>
            </div>
            <button
              onClick={onNext}
              disabled={!hasNextPage}
              tw="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-400 disabled:(opacity-30 cursor-not-allowed) hover:not-disabled:bg-gray-100 transition-colors"
            >
              <ChevronRightIcon tw="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateSessionDataTable;
